/**
 * @fileoverview Breadcrumbs navigation component
 * @active Used in current Next.js App Router routes
 *
 * This component renders the breadcrumbs navigation at the top of various pages.
 * It shows the navigation path and allows users to navigate back to parent routes.
 *
 * The component is used in both the active implementation and some deprecated components.
 *
 * @see {@link src/app/(main)/(home)/(research)/companies/page.tsx} - ACTIVE page that uses this
 */

"use client";

import { useEffect, useRef, useState } from "react";

import { Box, Flex, Text } from "@chakra-ui/react";
import Link from "next/link";
import { usePathname } from "next/navigation";

import ChevronRightSvg from "@/assets/svg/chevron-right-icon.svg";
import { h_400_16_normal_32 } from "@/styles/fontStyles";

/**
 * Interface for BreadcrumbsBlock component props
 */
interface BreadcrumbsBlockProps {
  /** Array of path segments to display in the breadcrumbs */
  arrayPath: string[];
  /** Name of the current object/page */
  objectName: string | React.ReactNode;
}

/**
 * Breadcrumbs navigation component that displays the current path hierarchy
 *
 * @param {BreadcrumbsBlockProps} props - Component props
 * @returns {JSX.Element} - Rendered breadcrumbs component
 *
 * @see {@link src/components/companies/BreadCrumbsBlockRender} - Wrapper in Companies.tsx
 * @see {@link src/app/(main)/(home)/(research)/companies/page.tsx} - Usage in App Router
 */
export const BreadcrumbsBlock = ({
  arrayPath,
  objectName,
}: BreadcrumbsBlockProps) => {
  const pathName = usePathname();

  const basePath = {
    Invest: { title: "Invest", path: "/invest", alt: "Invest page" },
    Home: { title: "Home", path: "/", alt: "Home Page" },
    Research: { title: "Research", path: "/companies", alt: "HomePage" },
    Withdrawal: {
      title: "Withdrawal",
      path: "/invest/withdrawal",
      alt: "Withdrawal Page",
    },
    My_Investments: {
      title: "My Investments",
      path: "/my_investments",
      alt: "My Investments",
    },
    Ongoing_Investments: {
      title: "Ongoing Investments",
      path: "/invest/ongoing_investments",
    },
    Companies: {
      title: "Companies",
      path: "/companies",
      alt: "Companies Page",
    },
    Investors: {
      title: "Investors",
      path: "/investors",
      alt: "Investors Page",
    },
    "Investment Firms": {
      title: "Investment Firms",
      path: "/investors",
      alt: "Investment Firms Page",
    },
    People: { title: "People", path: "/people", alt: "People Page" },
    "Asset-Managers": {
      title: "Asset Managers",
      path: "/investors",
      alt: "Asset-Managers Page",
    },
    ViewFunds: {
      title: "View Funds",
      path: "/invest/view-funds",
      alt: "View Funds Page",
    },
    Person: { title: "Person", path: "/people", alt: "Person Page" },
    "My investments": {
      title: "My investments",
      path: "/invest",
      alt: "My investments Page",
    },
    data: {
      title: objectName,
      path: pathName,
      alt: `${objectName} Page`,
    },
    Search: {
      title: "Search",
      path: "/search",
      alt: "Search Page",
    },
  };

  // TypeScript refs and state with proper types
  const refMark = useRef<HTMLDivElement | null>(null);
  const [lastElWidth, setLastElWidth] = useState<number | null>(null);

  useEffect(() => {
    const handleResize = () => {
      if (refMark.current) {
        setLastElWidth(refMark.current.offsetWidth);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Helper function for styling the last breadcrumb item
  const styleForLastItem = (index: number, length: number) => {
    if (index === length - 1) {
      return {
        color: "gray",
        cursor: "default",
        position: "absolute" as const,
        top: "0",
        left: "0",
        whiteSpace: "nowrap" as const,
        // Converting to string with 'px' for Chakra UI props compatibility
        w: lastElWidth ? `${lastElWidth}px` : undefined,
        isTruncated: true,
      };
    }
    return {};
  };

  return (
    <Flex align={"center"}>
      {arrayPath?.length &&
        arrayPath.map((item, index) => {
          return (
            <Flex
              key={`${basePath[item]?.title}-${index}`}
              w={index === arrayPath.length - 1 ? "100%" : "fit-content"}
              h={"20px"}
              flexDir={"column"}
              position={"relative"}
            >
              {index === arrayPath.length - 1 && (
                <Box h={"2px"} w={"100%"} ref={refMark}></Box>
              )}
              <Flex w={"100%"} overflow={"hidden"}>
                <Link
                  href={basePath[item]?.path || ""}
                  aria-label={basePath[item]?.alt} // Using aria-label instead of alt
                >
                  <Text
                    color={"blackColor"}
                    {...h_400_16_normal_32}
                    pt={"3px"}
                    cursor={"pointer"}
                    {...styleForLastItem(index, arrayPath.length)}
                    whiteSpace={"nowrap"}
                  >
                    {basePath[item]?.title}
                  </Text>
                </Link>
                {index !== arrayPath.length - 1 && <ChevronRightSvg />}
              </Flex>
            </Flex>
          );
        })}
    </Flex>
  );
};
